/**
 * Camada HTTP unica para o backend do Report Manager.
 *
 * Diferenca em relacao ao frontend Streamlit: aqui quem chama a API e o NAVEGADOR. Por isso
 * `VITE_API_BASE_URL` precisa ser a URL PUBLICA da API (incluindo o prefixo `/api/v1`) e a origem
 * deste app precisa estar em `CORS_ORIGINS` no backend.
 *
 * NUNCA colocar segredo em variavel `VITE_*`: ela vai embutida no bundle e e legivel por qualquer
 * visitante. A app key `ak-` do Auth continua existindo so no backend.
 */

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')

const TOKEN_KEY = 'report-manager.access_token'

/** Emitido quando o backend recusa a sessao; quem cuida da tela decide o que fazer. */
export const UNAUTHORIZED_EVENT = 'manager:unauthorized'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    /**
     * O `detail` ORIGINAL, quando o backend manda um objeto em vez de texto.
     *
     * A falha do PowerBI vem estruturada (`stage`, `powerbi_error_code`, `request_id`,
     * `activity_id`...), e foi esse payload que permitiu diagnosticar a falha de capacidade em
     * 2026-07-01. Achatar tudo em string perderia justamente os campos que se leva para um
     * chamado na Microsoft.
     */
    readonly payload?: unknown,
  ) {
    super(detail)
    this.name = 'ApiError'
  }
}

export function getToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY)
  } catch {
    // Navegador com armazenamento bloqueado: segue sem sessao em vez de quebrar a tela.
    return null
  }
}

export function setToken(token: string | null): void {
  try {
    if (token === null) sessionStorage.removeItem(TOKEN_KEY)
    else sessionStorage.setItem(TOKEN_KEY, token)
  } catch {
    /* idem */
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken()
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
      ...init?.headers,
    },
  })

  if (response.status === 401) {
    // Sessao expirada ou ausente: limpa o token e avisa a aplicacao, para a tela voltar ao login
    // em vez de ficar parada num erro que a pessoa nao consegue resolver.
    setToken(null)
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT))
  }

  if (!response.ok) {
    const { texto, bruto } = await readDetail(response)
    throw new ApiError(response.status, texto, bruto)
  }

  return (await response.json()) as T
}

/** O backend responde `{ detail: ... }`; um corpo que nao seja JSON nao pode derrubar o erro. */
async function readDetail(response: Response): Promise<{ texto: string; bruto: unknown }> {
  const semCorpo = { texto: `${response.status} ${response.statusText}`, bruto: undefined }
  try {
    const payload: unknown = await response.json()
    if (payload !== null && typeof payload === 'object' && 'detail' in payload) {
      const detail = (payload as { detail: unknown }).detail
      return {
        texto: typeof detail === 'string' ? detail : JSON.stringify(detail),
        bruto: detail,
      }
    }
    return semCorpo
  } catch {
    return semCorpo
  }
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path)
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) })
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body) })
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' })
}

/** As listagens do backend vem em `{ rows: [...] }`; o login vem no corpo direto. */
export interface Envelope<T> {
  rows: T[]
}
